import React, { useState, useEffect } from 'react';
import { baseUrl, assetFullLabel } from '../../shared';
import dayjs from 'dayjs';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import WorkshopJobModal from './WorkshopJobModal';
import TeardownPhotos from './TeardownPhotos';
import CalibrationsTab from './CalibrationsTab';
import {
  STATUS_COLOR, STATUS_LABELS,
  APPROVAL_COLOR, APPROVAL_LABELS,
  JOB_TYPE_LABELS,
} from '../../pages/workshop/index';
import { StatusChip } from '../ui';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

function DetailRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function TeardownForm({ jobId, teardown, onSaved }) {
  const EMPTY = {
    customerNotes: '',
    assessment: '',
    recommendation: '',
    repairableItem: false,
    replacementQuoted: false,
    partsRequired: false,
    partsDetail: '',
    totalCost: '',
  };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (teardown) {
      setForm({
        customerNotes: teardown.customerNotes || '',
        assessment: teardown.assessment || '',
        recommendation: teardown.recommendation || '',
        repairableItem: !!teardown.repairableItem,
        replacementQuoted: !!teardown.replacementQuoted,
        partsRequired: !!teardown.partsRequired,
        partsDetail: teardown.partsDetail || '',
        totalCost: teardown.totalCost || '',
      });
    } else {
      setForm(EMPTY);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teardown]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const setCheck = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.checked }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(baseUrl + 'workshop/jobs/' + jobId + '/teardown', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          totalCost: form.totalCost !== '' ? form.totalCost : null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        onSaved(data);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid container spacing={2} sx={{ pt: 1 }}>
      <Grid item xs={12}>
        <TextField
          size="small" fullWidth multiline rows={3}
          label="Notes from Customer" value={form.customerNotes} onChange={set('customerNotes')}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          size="small" fullWidth multiline rows={3}
          label="Assessment" value={form.assessment} onChange={set('assessment')}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          size="small" fullWidth multiline rows={3}
          label="Recommendation" value={form.recommendation} onChange={set('recommendation')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={<Checkbox checked={form.repairableItem} onChange={setCheck('repairableItem')} />}
          label="Repairable item"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={<Checkbox checked={form.replacementQuoted} onChange={setCheck('replacementQuoted')} />}
          label="Replacement quoted"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={<Checkbox checked={form.partsRequired} onChange={setCheck('partsRequired')} />}
          label="Parts required"
        />
      </Grid>
      {form.partsRequired && (
        <Grid item xs={12}>
          <TextField
            size="small" fullWidth multiline rows={2}
            label="Parts Detail" value={form.partsDetail} onChange={set('partsDetail')}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={4}>
        <TextField
          size="small" fullWidth type="number"
          label="Total Cost (Ex GST $)" value={form.totalCost} onChange={set('totalCost')}
        />
      </Grid>
      <Grid item xs={12}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : teardown ? 'Save Teardown' : 'Create Teardown'}
        </Button>
      </Grid>
    </Grid>
  );
}

function TeardownSection({ teardown, jobId, canManage }) {
  const [generating, setGenerating] = React.useState(false);
  const [reportUrl, setReportUrl] = React.useState(null);
  const [reportError, setReportError] = React.useState('');

  const generateReport = async () => {
    setGenerating(true); setReportError('');
    try {
      const r = await fetch(baseUrl + 'workshop/jobs/' + jobId + '/teardown/report', {
        method: 'POST', headers: authHeaders(),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Failed');
      setReportUrl(baseUrl + data.proxyUrl);
    } catch (e) { setReportError(e.message); }
    setGenerating(false);
  };

  return (
    <Grid container spacing={2} sx={{ pt: 1 }}>
      <Grid item xs={12}><DetailRow label="Notes from Customer" value={teardown.customerNotes} /></Grid>
      <Grid item xs={12}><DetailRow label="Assessment" value={teardown.assessment} /></Grid>
      <Grid item xs={12}><DetailRow label="Recommendation" value={teardown.recommendation} /></Grid>
      <Grid item xs={12} sm={4}><DetailRow label="Repairable Item" value={teardown.repairableItem ? 'Yes' : 'No'} /></Grid>
      <Grid item xs={12} sm={4}><DetailRow label="Replacement Quoted" value={teardown.replacementQuoted ? 'Yes' : 'No'} /></Grid>
      <Grid item xs={12} sm={4}><DetailRow label="Parts Required" value={teardown.partsRequired ? 'Yes' : 'No'} /></Grid>
      {teardown.partsDetail && <Grid item xs={12}><DetailRow label="Parts Detail" value={teardown.partsDetail} /></Grid>}
      {teardown.totalCost != null && <Grid item xs={12}><DetailRow label="Total Cost (Ex GST)" value={`$${Number(teardown.totalCost).toFixed(2)}`} /></Grid>}
      <Grid item xs={12}>
        <TeardownPhotos jobId={jobId} canManage={canManage} />
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <Button
            variant="outlined" size="small"
            onClick={generateReport}
            disabled={generating}
            startIcon={generating ? <CircularProgress size={14} /> : null}
          >
            {generating ? 'Generating…' : 'Generate Teardown Report'}
          </Button>
          {reportUrl && (
            <Button variant="contained" size="small" onClick={async () => {
              const r = await fetch(reportUrl, { headers: authHeaders() });
              const blob = await r.blob();
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
            }}>
              Open Report PDF
            </Button>
          )}
          {reportError && <Typography variant="caption" color="error">{reportError}</Typography>}
        </Box>
      </Grid>
    </Grid>
  );
}

export default function WorkshopJobDetailModal({ open, onClose, job, companies, canManage, onSaved, onUpdated, onDeleted }) {
  const [tab, setTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [approvalAnchor, setApprovalAnchor] = useState(null);
  const [patching, setPatching] = useState(false);

  useEffect(() => { if (open) setTab(0); }, [open]);

  const handlePatch = async (field, value) => {
    setPatching(true);
    setStatusAnchor(null);
    setApprovalAnchor(null);
    try {
      const res = await fetch(baseUrl + 'workshop/jobs/' + job.id, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      if (res.ok) onUpdated(await res.json());
    } finally { setPatching(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this job? This cannot be undone.')) return;
    setDeleting(true);
    await fetch(baseUrl + 'workshop/jobs/' + job.id, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    setDeleting(false);
    onDeleted();
  };

  const handleTeardownSaved = async (savedTeardown) => {
    const res = await fetch(baseUrl + 'workshop/jobs/' + job.id, { headers: authHeaders() });
    const updated = await res.json();
    onUpdated(updated);
  };

  if (!job) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
              {job.customer || 'Job'}{assetFullLabel(job) ? ` · ${assetFullLabel(job)}` : ''}
            </Typography>
            <Chip
              label={JOB_TYPE_LABELS[job.jobType] || job.jobType}
              size="small" variant="outlined"
            />
            <StatusChip
              domain="job"
              value={job.status}
              label={STATUS_LABELS[job.status] || job.status}
              size="md"
              onClick={canManage ? (e) => setStatusAnchor(e.currentTarget) : undefined}
              style={{ cursor: canManage ? 'pointer' : 'default' }}
            />
            {job.approval && (
              <StatusChip
                domain="approval"
                value={job.approval}
                label={APPROVAL_LABELS[job.approval] || job.approval}
                size="md"
                onClick={canManage ? (e) => setApprovalAnchor(e.currentTarget) : undefined}
                style={{ cursor: canManage ? 'pointer' : 'default' }}
              />
            )}
            {patching && <CircularProgress size={16} />}
          </Box>
        </DialogTitle>

        {/* Status picker */}
        <Menu anchorEl={statusAnchor} open={!!statusAnchor} onClose={() => setStatusAnchor(null)}>
          {Object.entries(STATUS_LABELS).map(([val, label]) => (
            <MenuItem key={val} selected={job.status === val} onClick={() => handlePatch('status', val)}
              sx={{ color: STATUS_COLOR[val], fontWeight: job.status === val ? 700 : 400 }}>
              {label}
            </MenuItem>
          ))}
        </Menu>

        {/* Approval picker */}
        <Menu anchorEl={approvalAnchor} open={!!approvalAnchor} onClose={() => setApprovalAnchor(null)}>
          {Object.entries(APPROVAL_LABELS).map(([val, label]) => (
            <MenuItem key={val} selected={job.approval === val} onClick={() => handlePatch('approval', val)}
              sx={{ color: APPROVAL_COLOR[val], fontWeight: job.approval === val ? 700 : 400 }}>
              {label}
            </MenuItem>
          ))}
        </Menu>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Job Details" />
          <Tab label={job.teardown ? 'Teardown' : 'Teardown (none)'} />
          <Tab label="Calibrations" />
        </Tabs>

        <DialogContent dividers>
          {tab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Asset</Typography>
                <Divider sx={{ mb: 1, mt: 0.5 }} />
                <DetailRow label="Customer" value={job.customer} />
                <DetailRow label="Asset ID" value={job.assetId} />
                <DetailRow label="Cat No" value={job.catNo} />
                <DetailRow label="Item" value={job.item} />
                <DetailRow label="Type" value={job.assetType} />
                <DetailRow label="Manufacturer" value={job.manufacturer} />
                <DetailRow label="Model" value={job.model} />
                <DetailRow label="Serial No" value={job.serialNo} />
                <DetailRow label="Site Location" value={job.siteLocation} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Job</Typography>
                <Divider sx={{ mb: 1, mt: 0.5 }} />
                <DetailRow label="Location" value={job.location === 'WORKSHOP' ? 'Workshop' : 'Site'} />
                <DetailRow label="Date Received" value={job.dateReceived ? dayjs(job.dateReceived).format('DD/MM/YYYY') : null} />
                <DetailRow label="Assigned To" value={job.jobAssignedTo} />
                <DetailRow label="Customer PO" value={job.customerPO} />
                <DetailRow label="Quote No" value={job.quoteNo} />
                <DetailRow label="Req No" value={job.reqNo} />
                <DetailRow label="Ref No" value={job.refNo} />
                <DetailRow label="Quoted Ex GST" value={job.quotedExGst != null ? `$${Number(job.quotedExGst).toFixed(2)}` : null} />
                <DetailRow label="Quoted Hours" value={job.quotedHrs} />
                <DetailRow label="Service Frequency" value={job.serviceFrequency} />
                <DetailRow label="Defect / Notes" value={job.defect} />
                <DetailRow label="Created By" value={job.createdBy} />
                <DetailRow label="Created" value={job.createdAt ? dayjs(job.createdAt).format('DD/MM/YYYY') : null} />
              </Grid>
            </Grid>
          )}

          {tab === 1 && canManage && (
            <>
              <TeardownForm
                jobId={job.id}
                teardown={job.teardown}
                onSaved={handleTeardownSaved}
              />
              {job.teardown && (
                <TeardownSection teardown={job.teardown} jobId={job.id} canManage={canManage} />
              )}
              {!job.teardown && (
                <TeardownPhotos jobId={job.id} canManage={canManage} />
              )}
            </>
          )}
          {tab === 1 && !canManage && !job.teardown && (
            <Typography color="text.secondary">No teardown recorded.</Typography>
          )}
          {tab === 1 && !canManage && job.teardown && (
            <TeardownSection teardown={job.teardown} jobId={job.id} canManage={false} />
          )}

          {tab === 2 && (
            <CalibrationsTab job={job} canManage={canManage} assetType={job.assetType} />
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', borderTop: '1px solid var(--border)' }}>
          <Box>
            {canManage && (
              <Button
                color="error" startIcon={<DeleteIcon />}
                onClick={handleDelete} disabled={deleting}
              >
                Delete
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} variant="outlined" color="inherit" sx={{ color: 'var(--muted)', borderColor: 'var(--border-strong)' }}>Close</Button>
            {canManage && (
              <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
                Edit Job
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <WorkshopJobModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        job={job}
        companies={companies}
        onSaved={async () => {
          setEditOpen(false);
          const res = await fetch(baseUrl + 'workshop/jobs/' + job.id, { headers: authHeaders() });
          const updated = await res.json();
          onUpdated(updated);
        }}
      />
    </>
  );
}
